import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { SortableContext, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useTranslation } from "react-i18next";
import type { DashboardMode, FilterPresetScope, FilterPresetSurface } from "@nexus/contracts";
import { Badge, GhostTextCombobox } from "@nexus/ui";
import {
  readActivePresetId,
  writeActivePresetId,
} from "@/features/dashboard/lib/active-preset-storage";
import { useDeleteFilterPreset, useReorderFilterPresets } from "@/lib/queries/filter-presets.queries";
import { SortablePresetRow } from "./SortablePresetRow";

export interface FilterPresetSelectorLabels {
  placeholder: string;
  triggerLabel: string;
  listHeading: string;
  searchPlaceholder: string;
  emptyMessage: string;
  listboxLabel: string;
  ariaLabel: string;
  deleteAria: string;
  saveHint?: string;
}

interface FilterPresetSelectorItem {
  id: string;
  name: string;
  scope?: FilterPresetScope;
  canManage?: boolean;
  canDelete?: boolean;
  canReorder?: boolean;
}

interface FilterPresetSelectorCoreProps<
  TPreset extends FilterPresetSelectorItem,
  TCreateInput extends { name: string },
> {
  surface: FilterPresetSurface;
  mode: DashboardMode;
  canListPresets: boolean;
  labels: FilterPresetSelectorLabels;
  filtersHydrated?: boolean;
  filterSignature: string;
  presets: TPreset[];
  createMutation?: UseMutationResult<TPreset, Error, TCreateInput>;
  buildPayload?: (mode: DashboardMode) => Record<string, unknown>;
  applyPreset: (preset: TPreset) => void;
  presetMatchesCurrent: (preset: TPreset) => boolean;
  onPresetApplied?: (preset: TPreset) => void;
  applySignal?: number;
  onPendingPresetChange?: (hasPending: boolean) => void;
  allowCreate?: boolean;
  selectPresetId?: string | null;
}

function filterPresetItems<TPreset extends FilterPresetSelectorItem>(
  presets: TPreset[],
  query: string,
): TPreset[] {
  const trimmed = query.trim();
  if (!trimmed) return presets;
  const lower = trimmed.toLowerCase();
  const prefixMatches = presets.filter((preset) => preset.name.toLowerCase().startsWith(lower));
  if (prefixMatches.length > 0) return prefixMatches;
  return presets.filter((preset) => preset.name.toLowerCase().includes(lower));
}

function findExactPreset<TPreset extends FilterPresetSelectorItem>(
  presets: TPreset[],
  query: string,
): TPreset | undefined {
  const lower = query.trim().toLowerCase();
  return presets.find((preset) => preset.name.toLowerCase() === lower);
}

function getGhostSuffix<TPreset extends FilterPresetSelectorItem>(
  query: string,
  preset: TPreset | null,
): string | null {
  if (!preset) return null;
  const trimmed = query.trim();
  if (!trimmed) return null;
  if (!preset.name.toLowerCase().startsWith(trimmed.toLowerCase())) return null;
  return preset.name.slice(trimmed.length);
}

function resolveListHighlightId<TPreset extends FilterPresetSelectorItem>(
  items: TPreset[],
  activePresetId: string | null,
): string | null {
  if (items.length === 0) return null;
  if (activePresetId && items.some((preset) => preset.id === activePresetId)) {
    return activePresetId;
  }
  return items[0]!.id;
}

function presetScope(preset: FilterPresetSelectorItem): FilterPresetScope {
  return preset.scope === "company" ? "company" : "personal";
}

function canDeletePreset(preset: FilterPresetSelectorItem): boolean {
  if (presetScope(preset) === "company") return preset.canDelete === true;
  return preset.canDelete !== false;
}

function canReorderListedPreset(preset: FilterPresetSelectorItem): boolean {
  if (presetScope(preset) === "company") return preset.canReorder === true;
  return preset.canReorder !== false;
}

export function FilterPresetSelectorCore<
  TPreset extends FilterPresetSelectorItem,
  TCreateInput extends { name: string },
>({
  surface,
  mode,
  canListPresets,
  labels,
  filtersHydrated = true,
  filterSignature,
  presets,
  createMutation,
  buildPayload,
  applyPreset,
  presetMatchesCurrent,
  onPresetApplied,
  applySignal,
  onPendingPresetChange,
  allowCreate = true,
  selectPresetId = null,
}: FilterPresetSelectorCoreProps<TPreset, TCreateInput>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [activePresetId, setActivePresetId] = useState<string | null>(null);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const deleteMutation = useDeleteFilterPreset(surface, mode);
  const reorderPersonal = useReorderFilterPresets(surface, mode, "personal");
  const reorderCompany = useReorderFilterPresets(surface, mode, "company");
  const reorderPending = reorderPersonal.isPending || reorderCompany.isPending;
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const searching = Boolean(inputValue.trim());

  const companyPresets = useMemo(
    () => presets.filter((preset) => presetScope(preset) === "company"),
    [presets],
  );
  const personalPresets = useMemo(
    () => presets.filter((preset) => presetScope(preset) === "personal"),
    [presets],
  );
  const companyIds = useMemo(() => companyPresets.map((preset) => preset.id), [companyPresets]);
  const personalIds = useMemo(() => personalPresets.map((preset) => preset.id), [personalPresets]);
  const canReorderCompany =
    !searching && companyIds.length > 1 && companyPresets.every(canReorderListedPreset);
  const canReorderPersonal = !searching && personalIds.length > 1;
  const canReorder = canReorderCompany || canReorderPersonal;
  const sortableIds = useMemo(
    () => [
      ...(canReorderCompany ? companyIds : []),
      ...(canReorderPersonal ? personalIds : []),
    ],
    [canReorderCompany, canReorderPersonal, companyIds, personalIds],
  );

  const filteredPresets = useMemo(
    () => filterPresetItems(presets, inputValue),
    [presets, inputValue],
  );
  const firstCompanyId = filteredPresets.find((preset) => presetScope(preset) === "company")?.id;
  const firstPersonalId = filteredPresets.find((preset) => presetScope(preset) === "personal")?.id;

  const highlightedPreset = useMemo(
    () => filteredPresets.find((preset) => preset.id === highlightedId) ?? filteredPresets[0] ?? null,
    [filteredPresets, highlightedId],
  );

  const ghostSuffix = useMemo(
    () => getGhostSuffix(inputValue, highlightedPreset),
    [inputValue, highlightedPreset],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next) {
        setHighlightedId(resolveListHighlightId(filterPresetItems(presets, inputValue), activePresetId));
      }
    },
    [activePresetId, inputValue, presets],
  );

  useLayoutEffect(() => {
    if (!open) return;
    setHighlightedId(resolveListHighlightId(filteredPresets, activePresetId));
  }, [activePresetId, filteredPresets, open]);

  useEffect(() => {
    if (!filtersHydrated || !activePresetId) return;
    const preset = presets.find((p) => p.id === activePresetId);
    if (!preset || !presetMatchesCurrent(preset)) {
      writeActivePresetId(surface, mode, null);
      setActivePresetId(null);
    }
  }, [activePresetId, filtersHydrated, presets, filterSignature, mode, presetMatchesCurrent, surface]);

  useEffect(() => {
    if (!filtersHydrated || activePresetId) return;
    const savedId = readActivePresetId(surface, mode);
    if (!savedId) return;
    const preset = presets.find((p) => p.id === savedId);
    if (preset && presetMatchesCurrent(preset)) {
      setActivePresetId(savedId);
    }
  }, [
    activePresetId,
    filtersHydrated,
    presets,
    filterSignature,
    mode,
    presetMatchesCurrent,
    surface,
  ]);

  const applyPresetFilters = useCallback(
    (preset: TPreset) => {
      applyPreset(preset);
      setActivePresetId(preset.id);
      setHighlightedId(preset.id);
      setInputValue("");
      setOpen(false);
      writeActivePresetId(surface, mode, preset.id);
      onPresetApplied?.(preset);
    },
    [applyPreset, mode, onPresetApplied, surface],
  );

  const commitOrder = useCallback(
    (scope: FilterPresetScope, ids: string[]) => {
      const mutation = scope === "company" ? reorderCompany : reorderPersonal;
      if (mutation.isPending) return;
      mutation.mutate(ids);
    },
    [reorderCompany, reorderPersonal],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const activePreset = presets.find((preset) => preset.id === String(active.id));
      const overPreset = presets.find((preset) => preset.id === String(over.id));
      if (!activePreset || !overPreset) return;
      const scope = presetScope(activePreset);
      if (scope !== presetScope(overPreset)) return;
      const blockIds = scope === "company" ? companyIds : personalIds;
      const oldIndex = blockIds.indexOf(String(active.id));
      const newIndex = blockIds.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      commitOrder(scope, arrayMove(blockIds, oldIndex, newIndex));
    },
    [commitOrder, companyIds, personalIds, presets],
  );

  const handleKeyboardMove = useCallback(
    (id: string, delta: -1 | 1) => {
      const preset = presets.find((row) => row.id === id);
      if (!preset) return;
      const scope = presetScope(preset);
      const blockIds = scope === "company" ? companyIds : personalIds;
      const index = blockIds.indexOf(id);
      const nextIndex = index + delta;
      if (index < 0 || nextIndex < 0 || nextIndex >= blockIds.length) return;
      commitOrder(scope, arrayMove(blockIds, index, nextIndex));
    },
    [commitOrder, companyIds, personalIds, presets],
  );

  const handleEnter = useCallback(() => {
    const trimmed = inputValue.trim();
    const existing = findExactPreset(presets, trimmed);
    if (existing) {
      applyPresetFilters(existing);
      return;
    }
    if (highlightedPreset) applyPresetFilters(highlightedPreset);
  }, [applyPresetFilters, highlightedPreset, inputValue, presets]);

  const lastSelectPresetIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!selectPresetId || selectPresetId === lastSelectPresetIdRef.current) return;
    const preset = presets.find((p) => p.id === selectPresetId);
    if (!preset) return;
    lastSelectPresetIdRef.current = selectPresetId;
    setActivePresetId(selectPresetId);
    setHighlightedId(selectPresetId);
    setInputValue("");
    writeActivePresetId(surface, mode, selectPresetId);
  }, [mode, presets, selectPresetId, surface]);

  const handleApplySignal = useCallback(async () => {
    if (!allowCreate) return;
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    if (createMutation?.isPending) return;

    const existing = findExactPreset(presets, trimmed);
    if (existing) {
      applyPresetFilters(existing);
      return;
    }

    if (!allowCreate || !createMutation || !buildPayload) return;

    const payload = buildPayload(mode);
    const created = await createMutation.mutateAsync({
      name: trimmed,
      ...payload,
    } as TCreateInput);
    setActivePresetId(created.id);
    setHighlightedId(created.id);
    setInputValue("");
    setOpen(false);
    writeActivePresetId(surface, mode, created.id);
  }, [
    allowCreate,
    applyPresetFilters,
    buildPayload,
    createMutation,
    inputValue,
    mode,
    presets,
    surface,
  ]);

  const lastHandledSignalRef = useRef(applySignal);
  useEffect(() => {
    if (applySignal === undefined) return;
    if (lastHandledSignalRef.current === applySignal) return;
    lastHandledSignalRef.current = applySignal;
    void handleApplySignal();
  }, [applySignal, handleApplySignal]);

  const comboboxItems = useMemo(
    () => filteredPresets.map((preset) => ({ id: preset.id, label: preset.name })),
    [filteredPresets],
  );

  const trimmedInput = inputValue.trim();
  const hasExactMatch = Boolean(findExactPreset(presets, trimmedInput));
  const hasPendingPreset =
    allowCreate && canListPresets && Boolean(trimmedInput && !hasExactMatch);

  useEffect(() => {
    onPendingPresetChange?.(hasPendingPreset);
  }, [hasPendingPreset, onPendingPresetChange]);

  useEffect(() => {
    return () => {
      onPendingPresetChange?.(false);
    };
  }, [onPendingPresetChange]);

  const activeLabel = presets.find((preset) => preset.id === activePresetId)?.name;
  const companyBadge = t("dashboard.filterPresets.companyBadge", "Company");
  const companyGroup = t("dashboard.filterPresets.companyGroup", "Company");
  const personalGroup = t("dashboard.filterPresets.personalGroup", "Personal");

  if (!canListPresets) return null;

  return (
    <GhostTextCombobox
      items={comboboxItems}
      inputValue={inputValue}
      onInputChange={(value) => {
        setInputValue(value);
        if (activePresetId) {
          writeActivePresetId(surface, mode, null);
          setActivePresetId(null);
        }
      }}
      highlightedId={highlightedId}
      onHighlightedIdChange={setHighlightedId}
      selectedId={activePresetId}
      ghostSuffix={ghostSuffix}
      onGhostAccept={() => {
        if (highlightedPreset) setInputValue(highlightedPreset.name);
      }}
      onSubmit={handleEnter}
      onSelectItem={(item) => {
        const preset = presets.find((p) => p.id === item.id);
        if (preset) applyPresetFilters(preset);
      }}
      onEscape={() => setHighlightedId(null)}
      open={open}
      onOpenChange={handleOpenChange}
      placeholder={activeLabel ?? labels.placeholder}
      triggerLabel={labels.triggerLabel}
      listHeading={labels.listHeading}
      searchPlaceholder={labels.searchPlaceholder}
      emptyMessage={labels.emptyMessage}
      listboxLabel={labels.listboxLabel}
      ariaLabel={labels.ariaLabel}
      saveHint={labels.saveHint}
      triggerClassName="max-w-[12rem]"
      contentClassName="w-56"
      wrapList={
        canReorder
          ? (list) => (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                  {list}
                </SortableContext>
              </DndContext>
            )
          : undefined
      }
      wrapItem={(item, node) => {
        const preset = presets.find((row) => row.id === item.id);
        const scope = preset ? presetScope(preset) : "personal";
        const heading =
          item.id === firstCompanyId
            ? companyGroup
            : item.id === firstPersonalId && companyPresets.length > 0
              ? personalGroup
              : null;
        const row =
          (scope === "company" ? canReorderCompany : canReorderPersonal) ? (
            <SortablePresetRow
              id={item.id}
              disabled={reorderPending}
              reorderAria={t("dashboard.filterPresets.reorder", "Reorder {{name}}", {
                name: item.label,
              })}
              onMove={handleKeyboardMove}
            >
              {node}
            </SortablePresetRow>
          ) : (
            node
          );
        if (!heading) return row;
        return (
          <>
            <li
              className="px-2 pt-1 pb-0.5 text-[10px] font-medium tracking-wide text-muted-foreground uppercase"
              aria-hidden
            >
              {heading}
            </li>
            {row}
          </>
        );
      }}
      renderItemActions={(item) => {
        const preset = presets.find((row) => row.id === item.id);
        if (!preset) return null;
        const isCompany = presetScope(preset) === "company";
        const showDelete = canDeletePreset(preset);
        return (
          <>
            {isCompany ? (
              <Badge variant="slate" className="h-4 shrink-0 px-1 text-[9px] leading-none">
                {companyBadge}
              </Badge>
            ) : null}
            {showDelete ? (
              <button
                type="button"
                className="ml-0.5 rounded p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                aria-label={labels.deleteAria}
                onClick={(event) => {
                  event.stopPropagation();
                  deleteMutation.mutate({ id: item.id, scope: presetScope(preset) });
                  if (activePresetId === item.id) {
                    writeActivePresetId(surface, mode, null);
                    setActivePresetId(null);
                    setInputValue("");
                  }
                }}
              >
                <Trash2 className="size-3" aria-hidden />
              </button>
            ) : null}
          </>
        );
      }}
    />
  );
}
